import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { fmtLog, fmtLogDay, orgToday } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PolicyEditor, { PolicyContent } from "@/components/PolicyEditor";
import DocumentDetailsDialog from "@/components/DocumentDetailsDialog";
import DraftApprovalPanel from "@/components/DraftApprovalPanel";
import AnnounceVersionDialog from "@/components/AnnounceVersionDialog";
import DocumentAiReviewPanel from "@/components/DocumentAiReviewPanel";
import { DocStatusBadges } from "@/pages/DocumentLibrary";
import {
  ArrowLeft, Lock, Mail, Sparkles, Download, Pencil, Loader2, FileUp, Save, Trash2, CheckCircle2, History, FilePen, Eye, Archive, AlertTriangle,
} from "lucide-react";
import { nextVersion as nextVersionLabel } from "@/lib/govDocs";
import { DOCS_ACCENT as ACCENT, LIBRARY_META, folderPath, indexFolders, fileToDataUrl, saveBlob, docLabel, fmtDay } from "@/lib/govDocs";

const MAX_DOCX_MB = 20;
const AUTOSAVE_IDLE_MS = 2500;     // save this long after the last keystroke
const AUTOSAVE_MAX_WAIT_MS = 30000; // ...but at least this often while typing continuously
const AUTOSAVE_RETRY_MS = 10000;   // retry after a failed save (e.g. network blip)
const isDirty = (d) => !!d && d.rev !== d.savedRev;

function Meta({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-slate-700 truncate">{children}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${active ? "border-[#6b4f8a] text-[#6b4f8a] font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
      {children}
    </button>
  );
}

export default function DocumentView({ library }) {
  const { docId } = useParams();
  const nav = useNavigate();
  const meta = LIBRARY_META[library];
  const [data, setData] = useState(null);           // GET /governance/documents/:id
  const [folders, setFolders] = useState([]);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "draft" ? "draft" : "current");
  // Draft being edited: rev counts local edits, savedRev is the last rev the server has.
  const [draft, setDraft] = useState(null);         // { html, summary, base, rev, savedRev, resetKey, source_filename }
  const [saveState, setSaveState] = useState("idle"); // idle | saving | error | conflict | unsafe
  const [conflictMsg, setConflictMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [bump, setBump] = useState("minor");      // Make Active: minor (1.3 -> 1.4) or major (2.0) update     // "publish" | "discard" | "retire" | "import"
  const [pendingImport, setPendingImport] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [announce, setAnnounce] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [showReview, setShowReview] = useState(true); // { id, version } of the Active version to email staff about     // archived version being viewed
  const fileRef = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;
  const inFlight = useRef(null);       // promise of the save in progress
  const dirtySince = useRef(null);     // when unsaved edits started (for the max-wait rule)
  const conflictRef = useRef(false);
  conflictRef.current = saveState === "conflict" || saveState === "unsafe";

  const load = useCallback(async (opts = {}) => {
    try {
      const r = await api.govDocument(docId);
      setData(r);
      if (r.draft && (opts.resetDraft || !draftRef.current)) {
        setDraft({ docId, html: r.draft.content_html, summary: r.draft.change_summary, base: r.draft.updated_at,
          rev: 0, savedRev: 0, resetKey: Date.now(), source_filename: null });
        setSaveState("idle"); setConflictMsg(""); dirtySince.current = null;
      }
      if (!r.draft) setDraft(null);
      return r;
    } catch (e) {
      toast.error(getApiError(e, "Couldn't load the document."));
      nav(`/documents/${library}`, { replace: true });
      return null;
    }
  }, [docId, library, nav]);

  useEffect(() => {
    setData(null); setDraft(null); draftRef.current = null;
    load({ resetDraft: true }).then((r) => { if (r && r.is_management && !r.active && r.draft) setTab("draft"); });
    api.govFolders(library).then((r) => setFolders(r.folders)).catch(() => {});
  }, [docId, library, load]);

  /** Save the draft if it has unsaved edits. Serialised: never two saves at once, and
   *  edits made while a save is in flight are picked up by the next one. Returns true if
   *  everything is saved. */
  const persist = useCallback(async ({ auto = false } = {}) => {
    while (inFlight.current) await inFlight.current.catch(() => {});
    const d = draftRef.current;
    if (!isDirty(d)) return true;
    if (conflictRef.current) return false;
    const snap = { ...d };
    setSaveState("saving");
    const p = api.govSaveDraft(snap.docId, { content_html: snap.html, change_summary: snap.summary, base_updated_at: snap.base,
      source_filename: snap.source_filename, autosave: auto });
    inFlight.current = p;
    try {
      const v = await p;
      const same = (cur) => cur && cur.docId === snap.docId;
      setDraft((cur) => (same(cur) ? { ...cur, base: v.updated_at, savedRev: snap.rev,
        source_filename: cur.source_filename === snap.source_filename ? null : cur.source_filename } : cur));
      if (same(draftRef.current)) draftRef.current = { ...draftRef.current, base: v.updated_at, savedRev: snap.rev };
      // The save response is the bare version; keep the approval status we already have.
      setData((x) => (x && x.document.id === snap.docId ? { ...x, draft: { ...v, approval: x.draft?.approval } } : x));
      setSaveState("idle");
      if (draftRef.current?.rev === snap.rev) dirtySince.current = null;
      return draftRef.current?.rev === snap.rev;
    } catch (e) {
      if (e?.response?.status === 409) {
        setSaveState("conflict");
        setConflictMsg(getApiError(e, "Someone else saved this draft."));
      } else {
        setSaveState("error");
        if (!auto) toast.error(getApiError(e, "Couldn't save the draft."));
      }
      return false;
    } finally {
      inFlight.current = null;
    }
  }, []);

  // Autosave: shortly after typing stops, or every 30s during continuous typing; retry after errors.
  useEffect(() => {
    if (!isDirty(draft) || saveState === "conflict" || saveState === "unsafe" || saveState === "saving") return undefined;
    if (!dirtySince.current) dirtySince.current = Date.now();
    const wait = saveState === "error" ? AUTOSAVE_RETRY_MS
      : Math.max(0, Math.min(AUTOSAVE_IDLE_MS, dirtySince.current + AUTOSAVE_MAX_WAIT_MS - Date.now()));
    const t = setTimeout(() => persist({ auto: true }), wait);
    return () => clearTimeout(t);
  }, [draft, saveState, persist]);

  // Leaving the page inside the app: save what's there (best effort). Closing/reloading the tab: warn.
  useEffect(() => () => {
    const pending = draftRef.current;
    if (!isDirty(pending) || conflictRef.current) return;
    // Wait for any save in flight (it refreshes draftRef's base), then send the rest.
    Promise.resolve(inFlight.current).catch(() => {}).then(() => {
      const d = draftRef.current?.docId === pending.docId ? draftRef.current : pending;
      if (!isDirty(d)) return;
      api.govSaveDraft(d.docId, { content_html: d.html, change_summary: d.summary, base_updated_at: d.base,
        source_filename: d.source_filename, autosave: true }).catch(() => {});
    });
  }, [docId]);
  const unsaved = isDirty(draft) || saveState === "saving";
  useEffect(() => {
    if (!unsaved) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [unsaved]);

  // Only real changes count as edits (the editor can report an "update" with identical content).
  const edit = (patch) => setDraft((d) => (Object.keys(patch).every((k) => d[k] === patch[k]) ? d : { ...d, ...patch, rev: d.rev + 1 }));

  const { byId } = useMemo(() => indexFolders(folders), [folders]);
  if (!data) return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const doc = data.document;
  const mgmt = data.is_management;
  const active = data.active;
  const crumbs = folderPath(byId, doc.folder_id);
  const overdue = active && doc.review_due && doc.review_due < orgToday();
  const firstVersion = !doc.latest_version;
  const nextVersion = nextVersionLabel(doc.latest_version, bump, doc.import_version);
  const locked = data.draft?.approval?.state === "pending";
  const approvers = (list) => (list || []).map((a) => a.name || a.username).join(", ");

  const run = async (key, fn) => {
    setBusy(key);
    try { return await fn(); } catch (e) { toast.error(getApiError(e, "Something went wrong.")); return undefined; } finally { setBusy(""); }
  };

  const saveNow = async () => {
    if (await persist()) toast.success("Draft saved");
  };

  const startDraft = () => run("start", async () => {
    await api.govStartDraft(doc.id);
    await load({ resetDraft: true });
    setTab("draft");
  });

  const pdf = (version, label) => run(`pdf-${version.id}`, async () => {
    const blob = await api.govVersionPdf(doc.id, version.id);
    saveBlob(blob, `${docLabel(doc)} ${label}.pdf`);
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) { toast.error("Choose a Word document (.docx)."); return; }
    if (file.size > MAX_DOCX_MB * 1024 * 1024) { toast.error(`Word documents must be under ${MAX_DOCX_MB} MB.`); return; }
    await run("import", async () => {
      const r = await api.govDocxToHtml(file.name, await fileToDataUrl(file));
      setPendingImport({ filename: file.name, ...r });
      setConfirm("import");
    });
  };

  const applyImport = () => {
    const imp = pendingImport;
    edit({ html: imp.content_html, resetKey: Date.now(), source_filename: imp.filename });
    imp.notes.forEach((n) => toast.warning(n));
    toast.success(`Imported ${imp.filename}. Check the formatting before making it Active.`);
    setPendingImport(null);
  };

  const doPublish = () => run("publish", async () => {
    if (!(await persist())) { toast.error("The draft couldn't be saved, so it wasn't made Active."); return; }
    const r = await api.govPublish(doc.id, bump);
    toast.success(r.archived_version ? `Version ${r.version} is now Active. Version ${r.archived_version} was archived.` : `Version ${r.version} is now Active.`);
    await afterPublish();
  });

  // After a version goes Active (the final approval, or Make Active): show it and offer to tell
  // staff straight away (the manager reviews the AI draft first).
  const afterPublish = async () => {
    const fresh = await load({ resetDraft: true });
    setTab("current");
    if (fresh?.active) setAnnounce({ id: fresh.active.id, version: fresh.active.version });
  };

  const runReview = async () => {
    setShowReview(true);
    setReviewing(true);
    try {
      if (!(await persist())) { toast.error("Save the draft first, then run the review."); return; }
      const r = await api.govAiReview(doc.id);
      setData((x) => (x ? { ...x, draft: { ...x.draft, ai_review: r } } : x));
    } catch (e) { toast.error(getApiError(e, "The AI review didn't work this time.")); }
    finally { setReviewing(false); }
  };

  const doDiscard = () => run("discard", async () => {
    while (inFlight.current) await inFlight.current.catch(() => {});
    draftRef.current = null;
    setDraft(null);
    const r = await api.govDiscardDraft(doc.id);
    toast.success("Draft discarded");
    if (r.document_deleted) { nav(`/documents/${library}${doc.folder_id ? `?folder=${doc.folder_id}` : ""}`); return; }
    await load({ resetDraft: true });
    setTab("current");
  });

  const doRetire = () => run("retire", async () => {
    await api.govRetire(doc.id);
    toast.success("Document archived. Staff can no longer see it.");
    await load();
  });

  const viewArchived = (v) => run(`view-${v.id}`, async () => setViewing(await api.govVersion(doc.id, v.id)));

  const backTo = `/documents/${library}${doc.folder_id ? `?folder=${doc.folder_id}` : ""}`;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="doc-view">
      <nav className="flex items-center gap-1 text-xs text-slate-500 flex-wrap mb-3">
        <Link to={backTo} className="inline-flex items-center gap-1 hover:text-[#6b4f8a]"><ArrowLeft className="h-3.5 w-3.5" />{meta.label}</Link>
        {crumbs.map((c) => (
          <span key={c.id} className="flex items-center gap-1"><span className="text-slate-300">/</span>
            <Link to={`/documents/${library}?folder=${c.id}`} className="hover:text-[#6b4f8a]">{c.name}</Link></span>
        ))}
      </nav>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {doc.code && <div className="text-xs font-semibold tracking-wider text-slate-400">{doc.code}</div>}
          <h1 className="text-2xl font-semibold font-brand text-slate-800">{doc.title}</h1>
          {mgmt && <div className="mt-1"><DocStatusBadges d={{ ...doc, awaiting_approval: locked }} /></div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {active && (
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => pdf(active, `v${active.version}`)} disabled={!!busy} data-testid="doc-pdf">
              {busy === `pdf-${active.id}` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Download PDF
            </Button>
          )}
          {mgmt && (
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => setDetailsOpen(true)} data-testid="doc-details">
              <Pencil className="h-4 w-4 mr-1" /> Details
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-white border border-slate-200 rounded-xl px-4 py-3 mt-4">
        <Meta label="Version">{active ? `Version ${active.version}` : "Not in effect"}</Meta>
        <Meta label="Effective">{active ? fmtLogDay(active.published_at) : "—"}</Meta>
        <Meta label="Next review">
          {doc.review_due ? <span className={overdue ? "text-rose-600 font-medium" : ""}>{fmtDay(doc.review_due)}{overdue ? " (overdue)" : ""}</span> : "Not scheduled"}
        </Meta>
        <Meta label="Owner">{doc.owner_name || "Not set"}</Meta>
      </div>

      {mgmt && (
        <div className="flex items-center gap-1 border-b border-slate-200 mt-5">
          <TabBtn active={tab === "current"} onClick={() => setTab("current")} testid="tab-current"><Eye className="inline h-3.5 w-3.5 mr-1" />Current version</TabBtn>
          <TabBtn active={tab === "draft"} onClick={() => setTab("draft")} testid="tab-draft">
            <FilePen className="inline h-3.5 w-3.5 mr-1" />Draft{data.draft && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />}
          </TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
            <History className="inline h-3.5 w-3.5 mr-1" />History{data.archived?.length ? ` (${data.archived.length})` : ""}
          </TabBtn>
        </div>
      )}

      {(!mgmt || tab === "current") && (
        <div className="mt-4">
          {active ? (
            <>
              <div className="bg-white border border-slate-200 rounded-xl px-6 py-5">
                <PolicyContent html={active.content_html} testid="doc-active-content" />
              </div>
              {mgmt && (
                <div className="flex items-center justify-between gap-3 mt-3 text-[11px] text-slate-400 flex-wrap">
                  <span>Made Active by {active.published_by} on {fmtLog(active.published_at)}
                    {active.approved_by?.length > 0 && <> · Approved by {approvers(active.approved_by)}</>}
                    {active.announced
                      ? <> · Emailed to {active.announced.sent ?? active.announced.recipients} of {active.announced.recipients} staff on {fmtLog(active.announced.at)}</>
                      : <> · <span className="text-amber-700">Staff haven't been emailed about this version</span></>}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAnnounce({ id: active.id, version: active.version })}
                      disabled={!!busy} data-testid="doc-announce">
                      <Mail className="h-3.5 w-3.5 mr-1" /> Email staff
                    </Button>
                    {!data.draft && (
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={startDraft} disabled={!!busy} data-testid="doc-start-draft">
                        {busy === "start" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FilePen className="h-3.5 w-3.5 mr-1" />} Start new draft
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 hover:text-rose-700" onClick={() => setConfirm("retire")} disabled={!!busy} data-testid="doc-retire">
                      <Archive className="h-3.5 w-3.5 mr-1" /> Archive document
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl px-6 py-10 text-center text-sm text-slate-500">
              {doc.retired ? "This document has been archived and is no longer in effect." : "This document hasn't been made Active yet."}
              {mgmt && !data.draft && (
                <div className="mt-3">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={startDraft} disabled={!!busy}>
                    <FilePen className="h-3.5 w-3.5 mr-1" /> Start a new draft
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {mgmt && tab === "draft" && (
        <div className="mt-4">
          {!data.draft ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl px-6 py-10 text-center text-sm text-slate-500">
              There is no draft.{" "}
              <Button size="sm" variant="outline" className="h-8 text-xs ml-2" onClick={startDraft} disabled={!!busy} data-testid="doc-start-draft-2">
                {busy === "start" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FilePen className="h-3.5 w-3.5 mr-1" />}
                {active ? `Start a new draft from version ${active.version}` : "Start a new draft"}
              </Button>
            </div>
          ) : draft && (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="text-[11px] text-slate-500">
                  {locked ? <span className="inline-flex items-center gap-1 font-medium" style={{ color: ACCENT }}><Lock className="h-3 w-3" />Locked while awaiting approval</span>
                    : saveState === "saving" ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Saving…</span>
                    : saveState === "error" ? <span className="text-rose-600 font-medium">Couldn't save. Retrying automatically…</span>
                    : isDirty(draft) ? <span className="text-amber-700">Unsaved changes</span>
                    : <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" />All changes saved · {data.draft.updated_by}, {fmtLog(data.draft.updated_at)}</span>}
                  {data.draft.source?.type === "docx" && <> · Imported from {data.draft.source.filename}</>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {!locked && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="draft-import">
                      {busy === "import" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileUp className="h-3.5 w-3.5 mr-1" />} Import from Word
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={runReview} disabled={!!busy || reviewing || !draft.html}
                    data-testid="draft-ai-review" title="Get AI suggestions, including conflicts with other current documents">
                    {reviewing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI review
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy || unsaved} title={unsaved ? "Wait for the draft to save" : "Download the saved draft as a PDF"}
                    onClick={() => pdf(data.draft, "DRAFT")} data-testid="draft-pdf">
                    <Download className="h-3.5 w-3.5 mr-1" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 hover:text-rose-700" onClick={() => setConfirm("discard")} disabled={!!busy} data-testid="draft-discard">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Discard
                  </Button>
                  {!locked && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={saveNow} disabled={!!busy || !isDirty(draft) || saveState === "conflict" || saveState === "unsafe"} data-testid="draft-save">
                      <Save className="h-3.5 w-3.5 mr-1" /> Save now
                    </Button>
                  )}
                </div>
              </div>
              {showReview && (
                <DocumentAiReviewPanel review={data.draft.ai_review} running={reviewing} onRun={runReview} onClose={() => setShowReview(false)}
                  docId={doc.id} locked={locked} beforeEdit={() => persist()}
                  onReview={(r) => setData((x) => (x?.draft ? { ...x, draft: { ...x.draft, ai_review: r } } : x))}
                  onDraftChanged={() => load({ resetDraft: true })} />
              )}
              {data.draft.approval && (
                <DraftApprovalPanel docId={doc.id} approval={data.draft.approval}
                  latestVersion={doc.latest_version} importVersion={doc.import_version}
                  disabled={!!busy || saveState === "conflict" || saveState === "unsafe" || (!locked && !draft.html)}
                  beforeSubmit={() => persist()} onChanged={() => load({ resetDraft: true })}
                  onPublished={afterPublish}
                  onMakeActive={() => { setBump(data.draft.approval.bump || "minor"); setConfirm("publish"); }} />
              )}
              {saveState === "conflict" && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 mb-3 text-sm text-rose-800" data-testid="draft-conflict">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium">Autosave paused</div>
                    <div className="text-xs">{conflictMsg} Your latest edits here have not been saved, so copy anything you need first.</div>
                  </div>
                  <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => load({ resetDraft: true })} data-testid="draft-reload">Reload draft</Button>
                </div>
              )}
              <div className="space-y-1 mb-3">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">What changed in this version?</Label>
                <Input value={draft.summary} maxLength={2000} className="h-9 text-sm bg-white" data-testid="draft-summary" disabled={locked}
                  placeholder={active ? "e.g. Updated the notice period for annual leave" : "e.g. First version"}
                  onChange={(e) => edit({ summary: e.target.value })} />
              </div>
              {saveState === "unsafe" && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 mb-3 text-sm text-rose-800" data-testid="draft-unsafe">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div><div className="font-medium">Autosave paused to protect this draft</div>
                    <div className="text-xs">The editor couldn't show some of this draft's {conflictMsg}, so saving now could remove them. The saved draft is unchanged. Please tell an Admin which document this is.</div></div>
                </div>
              )}
              <PolicyEditor value={draft.html} resetKey={draft.resetKey} editable={!locked}
                onContentLoss={(lost) => { setSaveState("unsafe"); setConflictMsg(lost.map((t) => (t === "img" ? "images" : "tables")).join(" and ")); }}
                onChange={(html) => edit({ html })} />
              <input ref={fileRef} type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={onFile} />
            </>
          )}
        </div>
      )}

      {mgmt && tab === "history" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
          {!data.archived.length ? (
            <div className="text-center py-12 text-sm text-slate-400">No archived versions in My Prydes Support yet.</div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="doc-history">
              {data.archived.map((v) => (
                <div key={v.id} className="flex items-start gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">Version {v.version}</div>
                    <div className="text-[11px] text-slate-500">
                      Active {fmtLogDay(v.published_at)} to {v.archived_at ? fmtLogDay(v.archived_at) : "—"} · made Active by {v.published_by}
                    </div>
                    {v.approved_by?.length > 0 && <div className="text-[11px] text-slate-500">Approved by {approvers(v.approved_by)}</div>}
                    {v.change_summary && <div className="text-xs text-slate-600 mt-0.5">{v.change_summary}</div>}
                  </div>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => viewArchived(v)} disabled={!!busy}>
                    {busy === `view-${v.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />} View
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => pdf(v, `v${v.version} ARCHIVED`)} disabled={!!busy}>
                    <Download className="h-3.5 w-3.5 mr-1" /> PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mgmt && tab === "history" && doc.legacy_history?.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="doc-legacy-history">
          <div className="px-4 py-2.5 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Before My Prydes Support{doc.imported_from?.filename ? ` · imported from ${doc.imported_from.filename}` : ""}
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
              <th className="px-4 py-1.5 font-semibold">Version</th><th className="px-2 py-1.5 font-semibold">Date</th>
              <th className="px-2 py-1.5 font-semibold">Reviewed by</th><th className="px-2 py-1.5 font-semibold">Changes</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {[...doc.legacy_history].reverse().map((h, i) => (
                <tr key={i} className="align-top">
                  <td className="px-4 py-1.5 font-medium text-slate-700 whitespace-nowrap">{h.version}</td>
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{h.date ? fmtDay(h.date) : h.date_text}</td>
                  <td className="px-2 py-1.5 text-slate-600">{h.reviewed_by}</td>
                  <td className="px-2 py-1.5 text-slate-600">{h.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-brand text-base">{docLabel(doc)}: version {viewing?.version} (archived)</DialogTitle>
          </DialogHeader>
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" /> This version has been superseded and is no longer in effect.
          </div>
          {viewing && <PolicyContent html={viewing.content_html} />}
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setViewing(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); setPendingImport(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "publish" && `Make version ${nextVersion} Active?`}
              {confirm === "discard" && "Discard this draft?"}
              {confirm === "retire" && "Archive this document?"}
              {confirm === "import" && "Replace the draft with the Word document?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-2">
                {confirm === "publish" && !firstVersion && (
                  <div className="flex gap-2 not-prose" data-testid="publish-bump">
                    {[["minor", "Minor update", "Small changes and corrections"], ["major", "Major update", "A rewrite or significant change"]].map(([k, l, d]) => (
                      <button key={k} type="button" onClick={() => setBump(k)} data-testid={`bump-${k}`}
                        className={`flex-1 text-left rounded-lg border px-3 py-2 ${bump === k ? "border-[#6b4f8a] bg-[#6b4f8a0f]" : "border-slate-200 hover:bg-slate-50"}`}>
                        <div className="text-sm font-medium text-slate-800">{l}: {nextVersionLabel(doc.latest_version, k)}</div>
                        <div className="text-[11px] text-slate-500">{d}</div>
                      </button>
                    ))}
                  </div>
                )}
                {confirm === "publish" && firstVersion && doc.import_version && (
                  <p className="text-xs text-slate-500">This keeps the version number from the imported document.</p>
                )}
                {confirm === "publish" && (active ? (
                  <>
                    <p>Version {nextVersion} will become the Active version and <strong>version {active.version} will be archived</strong>.</p>
                    <p>All staff will see the new version straight away. There can only be one Active version.</p>
                  </>
                ) : <p>This will become version {nextVersion} and all staff will be able to see it straight away.</p>)}
                {confirm === "discard" && (active || data.archived?.length
                  ? <p>All changes in the draft will be lost. The Active version is not affected.</p>
                  : <p>This document has never been made Active, so <strong>the whole document will be deleted</strong>.</p>)}
                {confirm === "retire" && <p>Version {active?.version} will be archived with no replacement. Staff will no longer see this document. Management can still view it under Show archived and start a new draft later.</p>}
                {confirm === "import" && pendingImport && (
                  <>
                    <p>The draft text will be replaced with the contents of <strong>{pendingImport.filename}</strong>. Nothing is saved until you click Save draft.</p>
                    {pendingImport.notes.map((n) => <p key={n} className="text-amber-700">{n}</p>)}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-action"
              className={confirm === "discard" || confirm === "retire" ? "bg-rose-600 hover:bg-rose-700" : "bg-[#6b4f8a] hover:bg-[#5a4175]"}
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                if (c === "publish") doPublish();
                else if (c === "discard") doDiscard();
                else if (c === "retire") doRetire();
                else if (c === "import") applyImport();
              }}>
              {confirm === "publish" ? "Make Active" : confirm === "discard" ? "Discard" : confirm === "retire" ? "Archive" : "Replace text"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AnnounceVersionDialog open={!!announce} docId={doc.id} versionId={announce?.id} versionLabel={announce?.version}
        onClose={() => setAnnounce(null)} onSent={() => load()} />

      <DocumentDetailsDialog open={detailsOpen} library={library} doc={doc} folders={folders}
        onClose={() => setDetailsOpen(false)}
        onSaved={() => { setDetailsOpen(false); load(); }} />
    </div>
  );
}
